import React, { FunctionComponent } from 'react';
import { SelectedTier } from '../../models/app';
import { SelectTierDialog, SelectTierDialogOptions } from './SelectTierDialog';

/**
 * Select tier button options interface. Configures properties required for selecting a tier.
 */
export interface SelectTierOptions {

	/**
	 * The select tier button text displayed to the user.
	 */
	label: string;

	/**
	 * The select tier button toolip text displayed to the user on hover of the button.
	 */
	tooltip: string;

	/**
	 * On tier selected handler
	 * @param tier The new tier selected.
	 */
	onSelect?: (tier: SelectedTier) => void;

	/**
	 * On tier selection cancelled (dialog closed / cancel button pressed).
	 * @param tier The new tier selected.
	 */
	onDialogCancel?: () => boolean;

	/**
	 * Currently selected tier.
	 */
	selectedTier?: SelectedTier;

	/**
	 * Root / minimum level tier that cannot be changed.
	 */
	rootTier?: SelectedTier;

	/**
	 * Determines whether tier 3 broker is required or not.
	 */
	requiresBroker?: boolean;

	/**
	 * Determines whether tier 2 group is required or not.
	 */
	requiresGroup?: boolean;

	/**
	 * Determines whether the tier selection component should be open by default.
	 */
	autoOpen?: boolean;
}

/**
 * Select tier button component. Outputs a select tier button that can be used to select a tier at page level,
 * or to select a tier for an input element within a form.
 * @param props Configuration properties.
 * @see SelectTierOptions
 */
export const SelectTierButton: FunctionComponent<SelectTierOptions> = (props: SelectTierOptions) => {

	let selectedTier = props.selectedTier;

	// create select tier dialog component options
	const options: SelectTierDialogOptions = {
		selectedTier: props.selectedTier,
		onSelect: (tier: SelectedTier) => {

			// on tier selected

			selectedTier = tier;

			// call provided handler, if provided
			if (props.onSelect)
				props.onSelect(tier);
		},
		requiresBroker: props.requiresBroker,
		requiresGroup: props.requiresGroup,
		autoOpen: props.autoOpen,
		onCancel: props.onDialogCancel
	};

	// open select tier dialog handler
	const handleOpen = () => {

		if (options.open) {
			options.open(selectedTier, props.rootTier);
		}
	};

	// output the button and select tier dialog elements
	return (
		<span className="select-tier">
			<button type="button" className="btn" onClick={handleOpen} title={props.tooltip}>{props.label ?? "Change"}</button>
			<SelectTierDialog options={options} />
		</span>
    );
}